import { styled } from '@uhg-abyss/web/tools/styled';
import React, { RefObject, useEffect, useRef, useState } from 'react';

export interface FloatingSectionProps {
  children: React.ReactNode;
  containerRef?: RefObject<HTMLDivElement>;
  alwaysFloat?: boolean;
  space?: number;
  position?: 'bottom' | 'top';
  css?: any;
  [key: string]: any;
}

const FloatingSectionRoot = styled('div', {
  zIndex: 99,
  position: 'sticky',
  backgroundColor: 'white',
  boxShadow: '0px 0px 6px rgba(0, 0, 0, 15%)',
  transitionProperty: 'box-shadow',
  transitionDuration: '250ms',
  transitionTimingFunction: 'ease-in-out',
  variants: {
    disableFloat: {
      true: {
        boxShadow: 'none',
      },
    },
    alwaysFloat: {
      true: {},
    },
  },
  compoundVariants: [
    {
      disableFloat: true,
      alwaysFloat: true,
      css: {
        transition: 'none',
        boxShadow: '0px 0px 6px rgba(0, 0, 0, 15%)',
      },
    },
  ],
});

const HiddenRef = styled('div', {
  visibility: 'hidden',
  position: 'relative',
});

export const FloatingSection = ({
  children,
  containerRef,
  space = 24,
  position = 'bottom',
  alwaysFloat = false,
  css: cssProp,
  ...rest
}: FloatingSectionProps) => {
  const intersectionRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(true);

  useEffect(() => {
    const node = intersectionRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Handle focus management - scroll focused element into view if behind floating section
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return undefined;

    const handleFocusIn = () => {
      const focusedElement = document.activeElement as HTMLElement;
      if (!focusedElement || !footerRef.current) return;

      const insideStickyFooter = footerRef.current.contains(focusedElement);
      if (isIntersecting || insideStickyFooter) return;

      const focusedPos = focusedElement.getBoundingClientRect();
      const footerPos = footerRef.current.getBoundingClientRect();

      const verticalOverlap =
        focusedPos.bottom >= footerPos.top &&
        focusedPos.top <= footerPos.bottom;
      const horizontalOverlap =
        focusedPos.right >= footerPos.left &&
        focusedPos.left <= footerPos.right;

      if (verticalOverlap && horizontalOverlap) {
        setTimeout(() => {
          focusedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }, 100);
      }
    };

    container.addEventListener('focusin', handleFocusIn);
    return () => container.removeEventListener('focusin', handleFocusIn);
  }, [containerRef, isIntersecting]);

  const hiddenRefStyle: React.CSSProperties = {
    ...(position === 'top' ? { bottom: space } : { top: space }),
  };

  const hiddenRefElement = (
    <HiddenRef ref={intersectionRef} style={hiddenRefStyle} />
  );

  // Extract 'floating-section-root' styles for root, transform remaining keys to nested selectors
  const { 'floating-section-root': rootStyles, ...restCss } = cssProp || {};
  const nestedCss = Object.entries(restCss).reduce((acc, [key, value]) => {
    acc[`& .${key}`] = value;
    return acc;
  }, {} as Record<string, any>);
  const rootCss = { [position]: space, ...rootStyles, ...nestedCss };

  return (
    <>
      {position === 'top' && hiddenRefElement}
      <FloatingSectionRoot
        alwaysFloat={alwaysFloat}
        className="floating-section-root"
        css={rootCss}
        disableFloat={isIntersecting}
        ref={footerRef}
        {...rest}
      >
        {children}
      </FloatingSectionRoot>
      {position === 'bottom' && hiddenRefElement}
    </>
  );
};

FloatingSection.displayName = 'FloatingSection';
